/**
 * Destino pós-login que sobrevive a um reload completo.
 *
 * `Auth.tsx` navega com `window.location.href`, não com `navigate()` — então o estado do
 * React morre no caminho. O `?redirect=` é a fonte primária; o sessionStorage é fallback para
 * quando o usuário passa por reset-password e perde a query string.
 *
 * ALLOWLIST DE PREFIXO É OBRIGATÓRIA. Um `?redirect=` aceito sem validação numa tela de login
 * é open-redirect — primitivo clássico de phishing ("entre no seu CRM" e cai em outro host).
 */

const KEY = 'vertto:post_login_redirect';

/** Só caminhos internos do portal. Ampliar isto exige pensar em open-redirect de novo. */
const ALLOWED_PREFIXES = ['/relatorios/'];

function isSafeTarget(path: string): boolean {
  if (!path.startsWith('/')) return false;   // absoluto/externo
  if (path.startsWith('//')) return false;   // protocol-relative → outro host
  if (path.includes('\\')) return false;     // normalização divergente entre browsers
  return ALLOWED_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function rememberPostLoginTarget(path: string): void {
  if (!isSafeTarget(path)) return;
  try {
    window.sessionStorage.setItem(KEY, path);
  } catch {
    // sessionStorage indisponível — o ?redirect= da URL ainda cobre o caso normal.
  }
}

/** Lê sem consumir. Usado pelo `isPortalContext`, que não pode ter efeito colateral. */
export function pendingPostLoginTarget(): string | null {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('redirect');
    if (fromQuery) {
      const decoded = decodeURIComponent(fromQuery);
      if (isSafeTarget(decoded)) return decoded;
    }
  } catch {
    // query malformada — cai para o sessionStorage
  }
  try {
    const stored = window.sessionStorage.getItem(KEY);
    if (stored && isSafeTarget(stored)) return stored;
  } catch {
    // ignorar
  }
  return null;
}

/** Lê E consome. Para quem vai efetivamente navegar. */
export function consumePostLoginTarget(): string | null {
  const target = pendingPostLoginTarget();
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignorar
  }
  return target;
}

/** Destino pós-login, com fallback. Consome o alvo guardado. */
export function resolvePostLoginTarget(fallback: string): string {
  return consumePostLoginTarget() ?? fallback;
}
